import React from 'react';
import { Tabs, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { CopyTab } from './CopyTab';
import { ResultsTab } from './ResultsTab';
import { SettingsTab } from './SettingsTab';
import { UpcomingTab } from './UpcomingTab';

const { Title, Text } = Typography;

const TABS = ['upcoming', 'copy', 'results', 'settings'] as const;
type TabKey = (typeof TABS)[number];

/**
 * Live Ops → Match Pushes: the queue of pushes the system will send by itself
 * (Upcoming), the copy, the results, and the settings with the sending switch.
 * `?fixture=<id>` (from the Cricket Calendar) shows only that match's pushes.
 */
export const MatchPushesPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: TabKey = TABS.includes(requested as TabKey) ? (requested as TabKey) : 'upcoming';
  const fixtureId = params.get('fixture') ?? undefined;

  const update = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        Match Pushes
      </Title>
      <Text type="secondary">
        Pushes sent around a cricket match: night before, 1 hour to go, toss, innings break and
        result.
      </Text>
      <Tabs
        style={{ marginTop: 12 }}
        activeKey={tab}
        onChange={(key) => update((next) => next.set('tab', key))}
        destroyInactiveTabPane
        items={[
          {
            key: 'upcoming',
            label: 'Upcoming',
            children: (
              <UpcomingTab
                fixtureId={fixtureId}
                onClearFixture={() => update((next) => next.delete('fixture'))}
                onOpenSettings={() => update((next) => next.set('tab', 'settings'))}
              />
            ),
          },
          { key: 'copy', label: 'Copy', children: <CopyTab /> },
          { key: 'results', label: 'Results', children: <ResultsTab /> },
          { key: 'settings', label: 'Settings', children: <SettingsTab /> },
        ]}
      />
    </div>
  );
};

export default MatchPushesPage;
